import type { SegyHeaderCoordsType } from '@core/core/types'
import type { Keys } from 'common/types'
import { useEffect } from 'react'

import { FILE_HEADER_SIZE } from '../../constants'
import { FormatMapping } from './data'
import {
  getBinaryHeader,
  getDataSampleFormatCodeSize,
  getDataTraceSize,
  getDelayRecordingTime,
  getFormatCode,
  getHeaderCoords,
  getNumberOfSamples,
  getSamplingStep,
  getTextualHeader,
  getTraceHeaders,
} from './helpers'
import type { UseSegyParserType } from './types'

export const readSegyBlob = async (blob: Blob): Promise<UseSegyParserType> => {
  const fileHeaderBlob = blob.slice(0, FILE_HEADER_SIZE, blob.type)
  const fileHeaderBuffer = await fileHeaderBlob.arrayBuffer()
  const textualHeader = getTextualHeader(new Uint8Array(fileHeaderBuffer))
  const binaryHeader = getBinaryHeader(fileHeaderBuffer)
  const traceHeaderSize = getDataTraceSize(fileHeaderBuffer)

  const traceHeaderBlob = blob.slice(
    FILE_HEADER_SIZE,
    FILE_HEADER_SIZE + traceHeaderSize,
    blob.type,
  )
  const traceHeaderBuffer = await traceHeaderBlob.arrayBuffer()

  const samplesNumber = getNumberOfSamples(fileHeaderBuffer)
  const dataSampleFormatCodeSize = getDataSampleFormatCodeSize(fileHeaderBuffer)
  const traceHeaders = getTraceHeaders(traceHeaderBuffer, dataSampleFormatCodeSize)

  const samplingStep = getSamplingStep(fileHeaderBuffer)
  const code = getFormatCode(fileHeaderBuffer)
  const formatCode = !code ? undefined : FormatMapping[code]

  const coords = getHeaderCoords(traceHeaderBuffer)
  let headersCoords: SegyHeaderCoordsType | undefined = undefined
  if (coords) {
    for (const key in coords) {
      headersCoords = {
        ...(headersCoords ?? {}),
        [key]: { offset: coords[key as Keys<typeof coords>], type: formatCode },
      }
    }
  }
  const delayRecordingTime = getDelayRecordingTime(traceHeaderBuffer)
  return {
    textualHeader,
    binaryHeader,
    traceHeaders,
    samplingStep,
    samplesNumber,
    formatCode,
    headersCoords,
    delayRecordingTime,
  }
}

export const useSegyParser = (
  file: Blob | undefined,
  onParseResult: (useSegyParserType: UseSegyParserType) => void,
) => {
  useEffect(() => {
    if (file) {
      readSegyBlob(file).then(onParseResult)
    }
  }, [file])
}
